import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { ENDPOINTS } from "utils/Constants";
import { currentUser } from "utils/CurrentUser";
import { notify } from "components/ToastNotification";
import { useI18n } from "i18n";

const apiLanguageByUi = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  zh: 'Chinese',
  ar: 'Arabic',
  ru: 'Russian',
  ja: 'Japanese',
  hi: 'Hindi',
  sq: 'Albanian',
};

export default function MyCoach() {
  const { t, language } = useI18n();
  const [goal, setGoal] = useState("Improve my weak areas and stay consistent");
  const [timeAvailableToday, setTimeAvailableToday] = useState(45);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textMuted = useColorModeValue("gray.600", "gray.300");
  const blockBorderColor = useColorModeValue("blue.100", "blue.700");
  const bannerBg = useColorModeValue(
    "linear-gradient(112deg, #0EA5E9 0%, #2563EB 45%, #0F172A 100%)",
    "linear-gradient(112deg, #22D3EE 0%, #1D4ED8 45%, #111827 100%)"
  );
  const accentBg = useColorModeValue("blue.50", "blue.900");

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        ENDPOINTS.GET_MY_COACH_PLAN,
        { goal, timeAvailableToday, language: apiLanguageByUi[language] || 'English' },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );
      if (!response?.data?.success) {
        notify(response?.data?.output || "Failed to generate your plan", "error");
        return;
      }
      setPlan(response.data.data);
    } catch (error) {
      notify("Failed to generate your coach plan.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialPlan = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          ENDPOINTS.GET_MY_COACH_PLAN,
          { goal, timeAvailableToday, language: apiLanguageByUi[language] || 'English' },
          { headers: { Authorization: `Bearer ${currentUser.token}` } }
        );
        if (!response?.data?.success) {
          notify(response?.data?.output || "Failed to generate your plan", "error");
          return;
        }
        setPlan(response.data.data);
      } catch (error) {
        notify("Failed to generate your coach plan.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const focusBlocks = useMemo(() => plan?.dailyPlan?.focusBlocks || [], [plan]);
  const weeklySchedule = useMemo(() => plan?.weeklyPlan?.schedule || [], [plan]);
  const wrongTopics = useMemo(() => plan?.signals?.wrongTopics || [], [plan]);
  const recommendations = useMemo(() => plan?.recommendations || [], [plan]);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px="20px" bg={pageBg}>
      <Box
        bg={bannerBg}
        borderRadius="20px"
        p={{ base: "20px", md: "28px" }}
        boxShadow="xl"
        mb="20px"
        color="white"
      >
        <Text fontSize="sm" textTransform="uppercase" letterSpacing="1px" opacity={0.9}>
          My Coach
        </Text>
        <Heading mt="2" fontSize={{ base: "2xl", md: "3xl" }}>
          {t("coach.title", "Personalized daily + weekly learning plan")}
        </Heading>
        <Text mt="3" fontSize="md" opacity={0.92}>
          {t("coach.subtitle", "Built from your progress, quiz trends, activity streak, overload, and your current goal.")}
        </Text>
        <HStack spacing="10px" mt="5" flexWrap="wrap">
          <Badge bg="whiteAlpha.300" px="10px" py="6px" borderRadius="full">
            Streak: {plan?.signals?.streak || 0} day(s)
          </Badge>
          <Badge bg="whiteAlpha.300" px="10px" py="6px" borderRadius="full">
            Active Days (30): {plan?.signals?.activeDaysLast30 || 0}
          </Badge>
          <Badge bg="whiteAlpha.300" px="10px" py="6px" borderRadius="full">
            Overload: {plan?.signals?.overloadLevel || "low"}
          </Badge>
        </HStack>
      </Box>

      <Grid templateColumns={{ base: "1fr", lg: "1.1fr 1.4fr" }} gap="20px">
        <GridItem>
          <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md">
            <Text fontSize="xl" fontWeight="700">{t("coach.planInputs", "Plan Inputs")}</Text>
            <Text color={textMuted} fontSize="sm" mt="1">
              {t("coach.tuneInputs", "Tune these and regenerate your plan.")}
            </Text>
            <Stack spacing="12px" mt="4">
              <Box>
                <Text fontSize="sm" mb="6px">{t("coach.goal", "Goal")}</Text>
                <Input
                  value={goal}
                  onChange={(event) => setGoal(event.target.value)}
                  placeholder="e.g. Become interview-ready in React"
                  bg={accentBg}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb="6px">{t("coach.timeAvailable", "Time available today (minutes)")}</Text>
                <NumberInput
                  min={10}
                  max={240}
                  value={timeAvailableToday}
                  onChange={(_, valueNumber) => setTimeAvailableToday(Number.isNaN(valueNumber) ? 45 : valueNumber)}
                >
                  <NumberInputField bg={accentBg} />
                </NumberInput>
              </Box>
              <Button
                variant="darkBrand"
                onClick={fetchPlan}
                isLoading={loading}
                loadingText={t("coach.generating", "Generating")}
              >
                {t("coach.generate", "Generate My Plan")}
              </Button>
            </Stack>
          </Box>

          <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md" mt="20px">
            <Text fontSize="xl" fontWeight="700">{t("coach.wrongTopics", "Wrong Topics to Fix")}</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px" mt="14px">
              {wrongTopics.length > 0 ? wrongTopics.map((topic) => (
                <Box key={topic} bg={accentBg} px="12px" py="10px" borderRadius="10px">
                  <Text fontSize="sm" fontWeight="600">{topic}</Text>
                </Box>
              )) : (
                <Text color={textMuted} fontSize="sm">No weak topics detected yet.</Text>
              )}
            </SimpleGrid>
          </Box>
        </GridItem>

        <GridItem>
          <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md">
            <Text fontSize="xl" fontWeight="700">{t("coach.todayPlan", "Today's Plan")}</Text>
            <Text color={textMuted} fontSize="sm" mt="1">
              {plan?.dailyPlan?.headline}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" mt="14px">
              {focusBlocks.length > 0 ? focusBlocks.map((block) => (
                <Box
                  key={`${block.skillName}-${block.minutes}`}
                  bg={accentBg}
                  p="14px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={blockBorderColor}
                >
                  <Text fontWeight="700">{block.skillName}</Text>
                  <Text fontSize="sm" color={textMuted} mt="1">{block.task}</Text>
                  <Flex mt="3" align="center" justify="space-between">
                    <Badge colorScheme="blue">{block.level}</Badge>
                    <Badge colorScheme="teal">{block.minutes} min</Badge>
                  </Flex>
                </Box>
              )) : (
                <Text color={textMuted} fontSize="sm">Start learning a skill to get actionable focus blocks.</Text>
              )}
            </SimpleGrid>
            <Box mt="14px" bg={useColorModeValue("gray.100", "gray.700")} p="12px" borderRadius="12px">
              <Text fontSize="sm" color={textMuted}>{plan?.dailyPlan?.shutdownRoutine}</Text>
            </Box>
          </Box>

          <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md" mt="20px">
            <Text fontSize="xl" fontWeight="700">{t("coach.weeklyBlueprint", "Weekly Blueprint")}</Text>
            <Text color={textMuted} fontSize="sm" mt="1">
              Theme: {plan?.weeklyPlan?.theme}
            </Text>
            <Text mt="2" fontWeight="600">{plan?.weeklyPlan?.weeklyTarget}</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" mt="14px">
              {weeklySchedule.map((entry) => (
                <Box key={`${entry.day}-${entry.date}`} bg={accentBg} p="12px" borderRadius="12px">
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="700">{entry.day}</Text>
                    <Badge colorScheme="purple">{entry.minutes} min</Badge>
                  </Flex>
                  <Text fontSize="sm" mt="2">{entry.focus}</Text>
                  <Text fontSize="xs" color={textMuted} mt="1">{entry.target}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </GridItem>
      </Grid>

      <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md" mt="20px" mb="20px">
        <Text fontSize="xl" fontWeight="700">{t("coach.recommendations", "Coach Recommendations")}</Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" mt="14px">
          {recommendations.length > 0 ? recommendations.map((recommendation) => (
            <Box key={recommendation} bg={accentBg} p="12px" borderRadius="12px">
              <Text fontSize="sm">{recommendation}</Text>
            </Box>
          )) : (
            <Text color={textMuted} fontSize="sm">No recommendations yet.</Text>
          )}
        </SimpleGrid>
      </Box>
    </Box>
  );
}
